import { NgModule } from "@angular/core";
import { Routes, RouterModule, UrlMatchResult, UrlSegment } from "@angular/router";
import { UnderdevelopmentComponent } from "./components/underdevelopment/underdevelopment.component";
import { MainPageComponent } from "./main-page/main-page.component";
import { RouteChildGuard } from "./route-child.guard";
import { RouteGuard } from "./route.guard";
import { LoginComponent } from "./user-management/login/login.component";
import { SignupComponent } from "./user-management/signup/signup.component";
import { PeriodicReportComponent } from "./periodic-report/periodic-report.component";
import { ForgotpassComponent } from "./user-management/forgotpass/forgotpass.component";
import { UserManagementSitesComponent } from "./userManagementSites/user-management-sites.component";
import { SiteSettingComponent } from "./site-setting/site-setting.component";
import { EquipmentAlertsSetupComponent } from "./site-setting/equipment-alerts-setup/equipment-alerts-setup.component";
import { DownloadpdfComponent } from "./downloadpdf/downloadpdf.component";
import { RoleAccessHostComponent } from "./plant-guardian-ai-host/plant-guardian-ai-host.component";

const roleMatcher = (segments: UrlSegment[]): UrlMatchResult | null => {
  if (!segments.length || segments[0].path !== "role") {
    return null;
  }

  return { consumed: segments };
};

const legacyRoleAccessMatcher = (
  segments: UrlSegment[]
): UrlMatchResult | null => {
  if (!segments.length || segments[0].path !== "plant-guardian-ai") {
    return null;
  }

  return { consumed: segments };
};

const routes: Routes = [
  { path: "login", component: LoginComponent, canActivate: [RouteGuard] },
  { path: "signup", component: SignupComponent, canActivate: [RouteGuard] },
  {
    path: "forgotpass",
    component: ForgotpassComponent,
    canActivate: [RouteGuard],
  },
  {
    // PurifAI SSO landing (/sso?t=<ticket>). Public — RouteGuard lets it
    // through via AuthAccessService.publicRoutes so the redeem runs before any
    // unauthenticated bounce to /login.
    path: "sso",
    loadComponent: () =>
      import("./user-management/sso-redeem/sso-redeem.component").then(
        (m) => m.SsoRedeemComponent
      ),
    canActivate: [RouteGuard],
  },
  {
    path: "privacy",
    loadComponent: () =>
      import("./compliance/legal/privacy-policy.component").then(
        (m) => m.PrivacyPolicyComponent
      ),
    canActivate: [RouteGuard],
  },
  {
    path: "terms",
    loadComponent: () =>
      import("./compliance/legal/terms.component").then((m) => m.TermsComponent),
    canActivate: [RouteGuard],
  },
  {
    path: "",
    component: MainPageComponent,
    children: [
      {
        matcher: roleMatcher,
        component: RoleAccessHostComponent,
        canActivate: [RouteGuard],
      },
      {
        matcher: legacyRoleAccessMatcher,
        component: RoleAccessHostComponent,
        canActivate: [RouteGuard],
      },
      {
        path: "",
        loadChildren: () =>
          import("./components/components.module").then(
            (m) => m.ComponentsModule
          ),
        canActivate: [RouteGuard],
        canActivateChild: [RouteChildGuard],
      },
      {
        path: "user-management-sites",
        component: UserManagementSitesComponent,
        canActivate: [RouteGuard],
        data: { roles: [16] },
      },
      {
        path: "newalerts",
        loadChildren: () =>
          import("./alerts/alerts.module").then((m) => m.AlertsModule),
        canActivate: [RouteGuard],
        canActivateChild: [RouteChildGuard],
      },
      {
        path: "category",
        loadChildren: () =>
          import("./category/category.module").then((m) => m.CategoryModule),
        canActivate: [RouteGuard],
        canActivateChild: [RouteChildGuard],
      },
      {
        path: "dashboard",
        loadChildren: () =>
          import("./dashboard/dashboard.module").then((m) => m.DashboardModule),
        canActivate: [RouteGuard],
        canActivateChild: [RouteChildGuard],
      },
      {
        path: "site/editsite/:siteid",
        component: SiteSettingComponent,
        canActivate: [RouteGuard],
      },
      {
        path: "site/editsite/:siteid/:corpid",
        component: SiteSettingComponent,
        canActivate: [RouteGuard],
      },
      {
        path: "site",
        loadChildren: () =>
          import("./site/site.module").then((m) => m.SiteModule),
        canActivate: [RouteGuard],
        canActivateChild: [RouteChildGuard],
      },
      {
        path: "categoryadminlib",
        loadChildren: () =>
          import("./category-admin-library/category-admin-library.module").then(
            (m) => m.CategoryAdminLibraryModule
          ),
        canActivate: [RouteGuard],
        canActivateChild: [RouteChildGuard],
      },
      {
        path: "opexmodule",
        loadChildren: () =>
          import("./opex-module-library/opex-module-library.module").then(
            (m) => m.OpexModuleLibraryModule
          ),
        canActivate: [RouteGuard],
        canActivateChild: [RouteChildGuard],
      },
      {
        path: "useradminlib",
        loadChildren: () =>
          import("./user-admin/user-admin.module").then(
            (m) => m.UserAdminModule
          ),
        canActivate: [RouteGuard],
        canActivateChild: [RouteChildGuard],
      },
      {
        path: "uploadslib",
        loadChildren: () =>
          import("./system-admin-uploads/system-admin-uploads.module").then(
            (m) => m.SystemAdminUploadsModule
          ),
        canActivate: [RouteGuard],
        canActivateChild: [RouteChildGuard],
      },
      {
        path: "systemadminlibrary",
        loadChildren: () =>
          import("./generic-library/generic-library.module").then(
            (m) => m.GenericLibraryModule
          ),
        canActivate: [RouteGuard],
        canActivateChild: [RouteChildGuard],
      },
      {
        path: "corporation",
        loadChildren: () =>
          import(
            "./corporation-admin-library/corporation-admin-library.module"
          ).then((m) => m.CorporationAdminLibraryModule),
        canActivate: [RouteGuard],
        canActivateChild: [RouteChildGuard],
      },
      {
        path: "mixdashboard",
        loadChildren: () =>
          import("./site-mixed-dashboard/site-mixed-dashboard.module").then(
            (m) => m.SiteMixedDashboardModule
          ),
        canActivate: [RouteGuard],
        canActivateChild: [RouteChildGuard],
      },
      {
        path: "parameteradmin",
        loadChildren: () =>
          import(
            "./parameter-admin-library/parameter-admin-library.module"
          ).then((m) => m.ParameterAdminLibraryModule),
        canActivate: [RouteGuard],
        canActivateChild: [RouteChildGuard],
      },
      {
        path: "moduleadmin",
        loadChildren: () =>
          import("./module-admin-library/module-admin-library.module").then(
            (m) => m.ModuleAdminLibraryModule
          ),
        canActivate: [RouteGuard],
        canActivateChild: [RouteChildGuard],
      },
      {
        path: "ai-topic-settings",
        loadChildren: () =>
          import("./ai-topic-settings/ai-topic-settings.module").then(
            (m) => m.AiTopicSettingsModule
          ),
        canActivate: [RouteGuard],
        canActivateChild: [RouteChildGuard],
      },
      {
        path: "userrolelibrary",
        loadChildren: () =>
          import("./user-role-library/user-role-library.module").then(
            (m) => m.UserRoleLibraryModule
          ),
        canActivate: [RouteGuard],
        canActivateChild: [RouteChildGuard],
      },
      {
        path: "reportlibrary",
        loadChildren: () =>
          import("./report-module-library/report-module-library.module").then(
            (m) => m.ReportModuleLibraryModule
          ),
        canActivate: [RouteGuard],
        canActivateChild: [RouteChildGuard],
      },
      {
        path: "uploadagent",
        loadChildren: () =>
          import("./upload-agent/upload-agent.module").then(
            (m) => m.UploadAgentModule
          ),
        canActivate: [RouteGuard],
        canActivateChild: [RouteChildGuard],
      },
      {
        path: "alerts",
        loadChildren: () =>
          import("./alert-module-library/alert-module-library.module").then(
            (m) => m.AlertModuleLibraryModule
          ),
        canActivate: [RouteGuard],
        canActivateChild: [RouteChildGuard],
      },
      {
        path: "notifications",
        loadChildren: () =>
          import("./notifications/notifications.module").then(
            (m) => m.NotificationsModule
          ),
        canActivate: [RouteGuard],
        canActivateChild: [RouteChildGuard],
      },
      {
        path: "equipment",
        loadChildren: () =>
          import("./equipment/equipment.module").then((m) => m.EquipmentModule),
        canActivate: [RouteGuard],
        canActivateChild: [RouteChildGuard],
      },
      {
        path: "ai-topic-settings",
        loadChildren: () =>
          import("./ai-topic-settings/ai-topic-settings.module").then((m) => m.AiTopicSettingsModule),
      },
      {
        path: "user-management-sites",
        component: UserManagementSitesComponent,
        canActivate: [RouteGuard],
        data: { roles: [16] },
      },
      {
        path: "site-setting",
        canActivate: [RouteGuard],
        component: SiteSettingComponent,
      },
      {
        path: "equipment-alerts-setup",
        canActivate: [RouteGuard],
        component: EquipmentAlertsSetupComponent,
      },
      {
        path: "safety/addmodule",
        component: UnderdevelopmentComponent,
        canActivate: [RouteGuard],
      },
      {
        path: "safety/editmodule",
        component: UnderdevelopmentComponent,
        canActivate: [RouteGuard],
      },
      {
        path: "equipment/addmodule",
        component: UnderdevelopmentComponent,
        canActivate: [RouteGuard],
      },
      {
        path: "equipment/editmodule",
        component: UnderdevelopmentComponent,
        canActivate: [RouteGuard],
      },
      {
        path: "periodicreport",
        component: PeriodicReportComponent,
        canActivate: [RouteGuard],
      },
      {
        path: "downloadpdf",
        component: DownloadpdfComponent,
        canActivate: [RouteGuard],
      },
      {
        path: "report-system",
        loadChildren: () =>
          import("./report-system/core/routes/report.routes").then(
            (m) => m.routes
          ),
        canActivate: [RouteGuard],
        canActivateChild: [RouteChildGuard],
      },
      {
        path: "account/privacy",
        loadComponent: () =>
          import("./compliance/my-data/my-data.component").then(
            (m) => m.MyDataComponent
          ),
        canActivate: [RouteGuard],
      },
      {
        path: "account/login-history",
        loadComponent: () =>
          import(
            "./compliance/login-history/login-history.component"
          ).then((m) => m.LoginHistoryComponent),
        canActivate: [RouteGuard],
      },
      {
        path: "account/audit-logs",
        loadComponent: () =>
          import("./compliance/audit-logs/audit-logs.component").then(
            (m) => m.AuditLogsComponent
          ),
        canActivate: [RouteGuard],
      }
    ],
    canActivate: [RouteGuard],
    canActivateChild: [RouteChildGuard],
  },

  { path: "**", redirectTo: "/" },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      useHash: false,
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
